import React, { useMemo, useState, useEffect } from 'react';
import { Typography, Grid, Button, Dialog, InputAdornment, TextField as MuiTextField, FormControlLabel, Checkbox } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import {
  DateInput,
  Edit,
  Labeled,
  NumberField,
  required,
  SimpleForm,
  TextInput,
  useRecordContext,
  useTranslate,
  EditBase,
  EditProps,
  useRefresh,
  useNotify,
  maxLength,
  number,
  regex,
  email,
  choices,
  SelectInput,
  FileInput,
  FileField,
  ReferenceInput,
  BooleanInput,
  TextField,
  AutocompleteInput,
  FormDataConsumer,
} from 'react-admin';
import { Client } from '../types';
import { ClientInputWithCreate } from '../components/Clients/ClientInputWithCreate';
import MuiPhoneNumber from 'material-ui-phone-number';
import { useFormContext } from 'react-hook-form';
import { Create } from 'react-admin';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { useLoadGoogleMaps } from '../hooks/useLoadGoogleMaps';
import { SIN_UNKNOWN } from './SinField';
import { DISSOLUTION_LOCATION_CHOICES, legacyDissolutionHint } from './dissolutionLocation';

const ClientEditBase = (props: any) => {
  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();

  const { setValue, getValues } = useFormContext();

  const onSuccess = () => {
    notify('Client updated');
    // onCancel();
  };

  const validateEmail = email();

  const record = useRecordContext<Client>();

  // Load Google Maps API properly to avoid ERR_BLOCKED_BY_ORB errors
  const { isLoaded: googleMapsLoaded, loadError: googleMapsError } = useLoadGoogleMaps(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  );

  const [phoneNumberValue, setPhoneNumberValue] = React.useState<string | null>(
    record?.phone_number
  );

  const handlePhoneNumberChange = (newValue: any) => {
    setValue('phone_number', newValue, { shouldDirty: true });
  };

  const [mobilePhoneNumberValue, setMobilePhoneNumberValue] = React.useState<
    string | null
  >(record?.mobile_phone_number);

  const handleMobilePhoneNumberChange = (newValue: any) => {
    setValue('mobile_phone_number', newValue, { shouldDirty: true });
  };

  // Google Places Autocomplete handler
  const [addressValue, setAddressValue] = React.useState<any>(
    record?.address ? { label: record.address, value: record.address } : null
  );

  const handleAddressChange = (selectedPlace: any) => {
    setAddressValue(selectedPlace);
    
    if (selectedPlace?.value) {
      // Check if Google Maps API is available (should be loaded by this point)
      if (!window.google?.maps?.places) {
        console.error('Google Maps API not available');
        return;
      }

      // Get detailed place information including postal code
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      service.getDetails(
        {
          placeId: selectedPlace.value.place_id,
          fields: ['address_components', 'formatted_address']
        },
        (place: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            let streetNumber = '';
            let route = '';
            let subpremise = ''; // apartment/unit number
            let city = '';
            let province = '';
            let postalCode = '';
            let country = '';

            // Parse address components from detailed response
            place.address_components?.forEach((component: any) => {
              const types = component.types;
              
              if (types.includes('street_number')) {
                streetNumber = component.long_name;
              } else if (types.includes('route')) {
                route = component.long_name;
              } else if (types.includes('subpremise')) {
                subpremise = component.long_name; // apartment/unit number
              } else if (types.includes('locality') || types.includes('sublocality_level_1')) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                province = component.short_name;
              } else if (types.includes('postal_code')) {
                postalCode = component.long_name.replace(/\s+/g, ''); // Remove all spaces
              } else if (types.includes('country')) {
                country = component.short_name;
              }
            });

            // Set form values with detailed data
            setValue('civic_number', streetNumber, { shouldDirty: true });
            setValue('address', route, { shouldDirty: true });
            setValue('apartment', subpremise, { shouldDirty: true });
            setValue('city', city, { shouldDirty: true });
            setValue('province', province, { shouldDirty: true });
            setValue('postal_code', postalCode, { shouldDirty: true });
            setValue('country', country, { shouldDirty: true });
            
            // Clear the autocomplete field after selection
            setTimeout(() => {
              setAddressValue(null);
            }, 100);
          }
        }
      );
    }
  };

  const validateMedicare = (value: any) => {
    if (value?.length > 0 && value?.length != 14) {
      return 'Medicare must be 14 characters';
    }
    return undefined;
  };

  // Canadian SIN validation using Luhn algorithm
  const validateCanadianSIN = (sin: string): boolean => {
    if (!sin || sin.length !== 9) return false;
    
    // Convert to array of digits
    const digits = sin.split('').map(Number);
    
    // Check if all digits are the same (invalid SIN)
    if (digits.every(digit => digit === digits[0])) return false;
    
    // Luhn algorithm for Canadian SIN
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      let digit = digits[i];
      if (i % 2 === 1) { // Even positions (0-indexed) are multiplied by 2
        digit *= 2;
        if (digit > 9) {
          digit = Math.floor(digit / 10) + (digit % 10);
        }
      }
      sum += digit;
    }
    
    return sum % 10 === 0;
  };

  // Format SIN as xxx xxx xxx
  const formatSIN = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Limit to 9 digits
    const limitedDigits = digits.substring(0, 9);
    
    // Format as xxx xxx xxx
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `${limitedDigits.substring(0, 3)} ${limitedDigits.substring(3)}`;
    } else {
      return `${limitedDigits.substring(0, 3)} ${limitedDigits.substring(3, 6)} ${limitedDigits.substring(6)}`;
    }
  };

  // SIN state management
  const [sinUnknown, setSinUnknown] = useState<boolean>(
    () => record?.sin === SIN_UNKNOWN
  );

  const [sinValue, setSinValue] = useState<string>(() => {
    const initialSin = record?.sin || '';
    // The 'UNKNOWN' sentinel has no digits, so formatSIN yields '' — the visible
    // field stays blank and the "SIN unknown" checkbox reflects the real state.
    return formatSIN(initialSin);
  });

  const [sinValidation, setSinValidation] = useState<'valid' | 'invalid' | null>(null);

  // Update SIN validation when value changes
  useEffect(() => {
    if (sinUnknown) {
      setSinValidation(null);
      return;
    }
    const digitsOnly = sinValue.replace(/\D/g, '');
    if (digitsOnly.length === 9) {
      const isValid = validateCanadianSIN(digitsOnly);
      setSinValidation(isValid ? 'valid' : 'invalid');
    } else {
      setSinValidation(null);
    }
  }, [sinValue, sinUnknown]);

  const handleSinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const formatted = formatSIN(newValue);
    setSinValue(formatted);

    // Typing a real SIN clears the "unknown" state.
    if (sinUnknown) {
      setSinUnknown(false);
    }

    // Store the digits-only version in the form
    const digitsOnly = formatted.replace(/\D/g, '');
    setValue('sin', digitsOnly, { shouldDirty: true });
  };

  const handleSinUnknownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSinUnknown(checked);
    if (checked) {
      // Clear the visible field and persist the sentinel so the customer's
      // "unknown" selection survives and other systems (aura-app) can read it.
      setSinValue('');
      setSinValidation(null);
      setValue('sin', SIN_UNKNOWN, { shouldDirty: true });
    } else {
      setValue('sin', '', { shouldDirty: true });
    }
  };

  const [createSpouseOpen, setCreateSpouseOpen] = useState(false);

  const handleCreateSpouseSuccess = (response: any) => {
    setCreateSpouseOpen(false);
    // Set the newly created spouse's ID as the spouse_id
    setValue('spouse_id', response.id, { shouldDirty: true });
  };

  return (
    <>
      {/* Basic Information */}
      <Grid container spacing={2} alignItems="center">

        <Grid item xs={12} sm={3}>
          <TextInput
            source="first_name"
            fullWidth
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextInput
            source="middle_name"
            label="Middle Name"
            fullWidth
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextInput
            source="last_name"
            fullWidth
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextInput
            source="maiden_name"
            label="Maiden Name"
            fullWidth
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <BooleanInput source="deceased" helperText={false} />
        </Grid>
      </Grid>

      {/* Personal Information */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <DateInput source="birthdate" fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextInput source="place_of_birth" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SelectInput
            source="sex"
            fullWidth
            helperText={false}
            choices={[
              { id: 'M', name: 'resources.clients.gender.male' },
              { id: 'F', name: 'resources.clients.gender.female' },
              { id: 'O', name: 'resources.clients.gender.other' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextInput
            source="weight"
            label="Estimated Weight (lbs)"
            fullWidth
            helperText={false}
          />
        </Grid>
      </Grid>

      {/* Contact Information */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextInput
            source="email"
            validate={validateEmail}
            fullWidth
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextInput
            hidden
            type="hidden"
            label={false}
            InputProps={{ disableUnderline: true }}
            helperText={false}
            sx={{ margin: 0 }}
            source="phone_number"
          />
          <MuiPhoneNumber
            label="Phone Number"
            defaultCountry={'ca'}
            value={phoneNumberValue}
            onChange={handlePhoneNumberChange}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextInput
            hidden
            type="hidden"
            label={false}
            InputProps={{ disableUnderline: true }}
            helperText={false}
            sx={{ margin: 0 }}
            source="mobile_phone_number"
          />
          <MuiPhoneNumber
            label="Mobile Phone Number"
            defaultCountry={'ca'}
            value={mobilePhoneNumberValue}
            onChange={handleMobilePhoneNumberChange}
          />
        </Grid>
      </Grid>

      {/* Address */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '0.75rem',
              color: 'rgba(0, 0, 0, 0.6)',
              fontWeight: 400,
              lineHeight: 1.4375,
              letterSpacing: '0.00938em'
            }}>
              Search Address
            </label>
            {googleMapsError ? (
              <div style={{
                minHeight: '56px',
                border: '1px solid rgba(211, 47, 47, 0.5)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '14px',
                color: 'rgba(211, 47, 47, 0.87)',
                fontSize: '16px',
                backgroundColor: 'rgba(211, 47, 47, 0.05)'
              }}>
                Address search unavailable: {googleMapsError.message}
              </div>
            ) : !googleMapsLoaded ? (
              <div style={{
                minHeight: '56px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '14px',
                color: 'rgba(0, 0, 0, 0.6)',
                fontSize: '16px'
              }}>
                Loading address search...
              </div>
            ) : (
              <GooglePlacesAutocomplete
                selectProps={{
                  value: addressValue,
                  onChange: handleAddressChange,
                  placeholder: 'Search for an address to auto-fill fields below...',
                  isClearable: true,
                  styles: {
                    control: (provided: any) => ({
                      ...provided,
                      minHeight: '56px',
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      fontSize: '16px',
                      '&:hover': {
                        borderColor: 'rgba(0, 0, 0, 0.87)',
                      },
                      '&:focus-within': {
                        borderColor: '#1976d2',
                        borderWidth: '2px',
                      }
                    }),
                    placeholder: (provided: any) => ({
                      ...provided,
                      color: 'rgba(0, 0, 0, 0.6)',
                    }),
                    menu: (provided: any) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }
                }}
                autocompletionRequest={{
                  componentRestrictions: { country: ['ca', 'us'] },
                  types: ['address']
                }}
              />
            )}
            
          </div>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextInput source="civic_number" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInput source="address" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextInput source="apartment" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextInput source="city" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextInput source="province" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextInput source="postal_code" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <AutocompleteInput
            source="country"
            choices={countryList}
            fullWidth
            helperText={false}
          />
        </Grid>
      </Grid>

      {/* Additional Information */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <TextInput source="occupation" fullWidth helperText={false} />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextInput source="industry" fullWidth helperText={false} />
        </Grid>

        <Grid item xs={12} sm={3}>
          <BooleanInput source="retired" helperText={false} />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextInput
            hidden
            type="hidden"
            label={false}
            InputProps={{ disableUnderline: true }}
            helperText={false}
            sx={{ margin: 0 }}
            source="sin"
          />
          <MuiTextField
            label="SIN"
            value={sinValue}
            onChange={handleSinChange}
            fullWidth
            disabled={sinUnknown}
            placeholder={sinUnknown ? 'Unknown' : '123 456 789'}
            inputProps={{
              maxLength: 11, // 9 digits + 2 spaces
            }}
            InputProps={{
              endAdornment: !sinUnknown && sinValidation && (
                <InputAdornment position="end">
                  {sinValidation === 'valid' ? (
                    <CheckCircle sx={{ color: 'green' }} />
                  ) : (
                    <Cancel sx={{ color: 'red' }} />
                  )}
                </InputAdornment>
              ),
            }}
            helperText={
              sinUnknown
                ? 'Marked unknown by customer'
                : sinValidation === 'invalid'
                ? 'Invalid Canadian SIN'
                : sinValue.replace(/\D/g, '').length > 0 && sinValue.replace(/\D/g, '').length < 9
                ? 'SIN must be 9 digits'
                : ''
            }
            error={!sinUnknown && sinValidation === 'invalid'}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={sinUnknown}
                onChange={handleSinUnknownChange}
                size="small"
              />
            }
            label="SIN unknown"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextInput source="drivers_license" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextInput
            source="medicare_number"
            fullWidth
            helperText={false}
            // validate={validateMedicare}
          />
        </Grid>
      </Grid>

      {/* Parent Information */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextInput
            source="parent1_first_name"
            fullWidth
            label="Parent 1 (Father) First Name"
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextInput
            source="parent1_last_name"
            fullWidth
            label="Parent 1 (Father) Last Name"
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextInput
            source="parent1_place_of_birth"
            fullWidth
            label="Parent 1 (Father) Place of Birth"
            helperText={false}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextInput
            source="parent2_first_name"
            fullWidth
            label="Parent 2 (Mother) First Name"
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextInput
            source="parent2_last_name"
            fullWidth
            label="Parent 2 (Mother) Last Name"
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextInput
            source="parent2_place_of_birth"
            fullWidth
            label="Parent 2 (Mother) Place of Birth"
            helperText={false}
          />
        </Grid>
      </Grid>

      {/* Spouse Information */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Spouse Information
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ReferenceInput
            reference="marital_statuses"
            source="marital_status_id"
            helperText={false}
          >
            <AutocompleteInput
              optionText="name"
              filterToQuery={(searchText) => ({ 'name@ilike': searchText })}
              fullWidth
              helperText={false}
            />
          </ReferenceInput>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={9}>
          <ReferenceInput
            reference="clients"
            source="spouse_id"
            helperText={false}
          >
            <AutocompleteInput
              optionText={(record) => 
                record.name + 
                (record.birthdate ? ` (${new Date(record.birthdate).getFullYear()})` : '')
              }
              filterToQuery={(searchText) => ({ 'name@ilike': `%${searchText}%`, is_pickable: true })}
              fullWidth
              helperText={false}
            />
          </ReferenceInput>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            onClick={() => setCreateSpouseOpen(true)}
            fullWidth
          >
            Create Spouse
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <DateInput 
            source="date_of_marriage" 
            fullWidth 
            helperText={false}
          />
          <BooleanInput source="date_of_marriage_unknown" helperText={false} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextInput 
            source="place_of_marriage" 
            fullWidth 
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DateInput 
            source="date_of_marriage_dissolution" 
            fullWidth 
            helperText={false}
          />
          <BooleanInput source="date_of_marriage_dissolution_unknown" helperText={false} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DissolutionLocationInput />
        </Grid>
      </Grid>

      {/* Notes */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <BooleanInput source="send_condolences" fullWidth helperText={false} />
        </Grid>
        <Grid item xs={12}>
          <TextInput source="note" fullWidth multiline helperText={false} />
        </Grid>
      </Grid>

      <Dialog
        open={createSpouseOpen}
        onClose={() => setCreateSpouseOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <Create
          resource="clients"
          mutationOptions={{
            onSuccess: handleCreateSpouseSuccess,
          }}
        >
          <SimpleForm>
            <ClientEditBase createName="" />
          </SimpleForm>
        </Create>
      </Dialog>
    </>
  );
};

const ClientInputOptions = (choice: any) => `${choice.name}`;

const OptionRenderer = (props: any) => {
  const record = useRecordContext();
  return record.id === '@@ra-create' ? (
    <div {...props}>{record.name}</div>
  ) : (
    <div {...props}>
      <span style={{ fontWeight: '600' }}>{record?.name}</span>{' '}
      {!!record.address && <span>/ {record.address}</span>}{' '}
      {!!record.phone_number && <span>/ {record.phone_number}</span>}
    </div>
  );
};

const inputText = (choice: any) => `${choice.name}`;

const matchSuggestion = (filter: any, choice: any) => {
  return choice.name.toLowerCase().includes(filter.toLowerCase());
  // || choice.last_name.toLowerCase().includes(filter.toLowerCase())
};

const countryList = [
  {
    id: 'CA',
    name: 'Canada',
  },
  {
    id: 'US',
    name: 'United States',
  },
  {
    id: 'AF',
    name: 'Afghanistan',
  },
  {
    id: 'AX',
    name: 'Åland Islands',
  },
  {
    id: 'AL',
    name: 'Albania',
  },
  {
    id: 'DZ',
    name: 'Algeria',
  },
  {
    id: 'AS',
    name: 'American Samoa',
  },
  {
    id: 'AD',
    name: 'Andorra',
  },
  {
    id: 'AO',
    name: 'Angola',
  },
  {
    id: 'AI',
    name: 'Anguilla',
  },
  {
    id: 'AQ',
    name: 'Antarctica',
  },
  {
    id: 'AG',
    name: 'Antigua and Barbuda',
  },
  {
    id: 'AR',
    name: 'Argentina',
  },
  {
    id: 'AM',
    name: 'Armenia',
  },
  {
    id: 'AW',
    name: 'Aruba',
  },
  {
    id: 'AU',
    name: 'Australia',
  },
  {
    id: 'AT',
    name: 'Austria',
  },
  {
    id: 'AZ',
    name: 'Azerbaijan',
  },
  {
    id: 'BS',
    name: 'Bahamas',
  },
  {
    id: 'BH',
    name: 'Bahrain',
  },
  {
    id: 'BD',
    name: 'Bangladesh',
  },
  {
    id: 'BB',
    name: 'Barbados',
  },
  {
    id: 'BY',
    name: 'Belarus',
  },
  {
    id: 'BE',
    name: 'Belgium',
  },
  {
    id: 'BZ',
    name: 'Belize',
  },
  {
    id: 'BJ',
    name: 'Benin',
  },
  {
    id: 'BM',
    name: 'Bermuda',
  },
  {
    id: 'BT',
    name: 'Bhutan',
  },
  {
    id: 'BO',
    name: 'Bolivia, Plurinational State of',
  },
  {
    id: 'BQ',
    name: 'Bonaire, Sint Eustatius and Saba',
  },
  {
    id: 'BA',
    name: 'Bosnia and Herzegovina',
  },
  {
    id: 'BW',
    name: 'Botswana',
  },
  {
    id: 'BV',
    name: 'Bouvet Island',
  },
  {
    id: 'BR',
    name: 'Brazil',
  },
  {
    id: 'IO',
    name: 'British Indian Ocean Territory',
  },
  {
    id: 'BN',
    name: 'Brunei Darussalam',
  },
  {
    id: 'BG',
    name: 'Bulgaria',
  },
  {
    id: 'BF',
    name: 'Burkina Faso',
  },
  {
    id: 'BI',
    name: 'Burundi',
  },
  {
    id: 'CV',
    name: 'Cabo Verde',
  },
  {
    id: 'KH',
    name: 'Cambodia',
  },
  {
    id: 'CM',
    name: 'Cameroon',
  },
  {
    id: 'KY',
    name: 'Cayman Islands',
  },
  {
    id: 'CF',
    name: 'Central African Republic',
  },
  {
    id: 'TD',
    name: 'Chad',
  },
  {
    id: 'CL',
    name: 'Chile',
  },
  {
    id: 'CN',
    name: 'China',
  },
  {
    id: 'CX',
    name: 'Christmas Island',
  },
  {
    id: 'CC',
    name: 'Cocos (Keeling) Islands',
  },
  {
    id: 'CO',
    name: 'Colombia',
  },
  {
    id: 'KM',
    name: 'Comoros',
  },
  {
    id: 'CG',
    name: 'Congo',
  },
  {
    id: 'CD',
    name: 'Congo, Democratic Republic of the',
  },
  {
    id: 'CK',
    name: 'Cook Islands',
  },
  {
    id: 'CR',
    name: 'Costa Rica',
  },
  {
    id: 'HR',
    name: 'Croatia',
  },
  {
    id: 'CU',
    name: 'Cuba',
  },
  {
    id: 'CW',
    name: 'Curaçao',
  },
  {
    id: 'CY',
    name: 'Cyprus',
  },
  {
    id: 'CZ',
    name: 'Czechia',
  },
  {
    id: 'CI',
    name: "Côte d'Ivoire",
  },
  {
    id: 'DK',
    name: 'Denmark',
  },
  {
    id: 'DJ',
    name: 'Djibouti',
  },
  {
    id: 'DM',
    name: 'Dominica',
  },
  {
    id: 'DO',
    name: 'Dominican Republic',
  },
  {
    id: 'EC',
    name: 'Ecuador',
  },
  {
    id: 'EG',
    name: 'Egypt',
  },
  {
    id: 'SV',
    name: 'El Salvador',
  },
  {
    id: 'GQ',
    name: 'Equatorial Guinea',
  },
  {
    id: 'ER',
    name: 'Eritrea',
  },
  {
    id: 'EE',
    name: 'Estonia',
  },
  {
    id: 'SZ',
    name: 'Eswatini',
  },
  {
    id: 'ET',
    name: 'Ethiopia',
  },
  {
    id: 'FK',
    name: 'Falkland Islands (Malvinas)',
  },
  {
    id: 'FO',
    name: 'Faroe Islands',
  },
  {
    id: 'FJ',
    name: 'Fiji',
  },
  {
    id: 'FI',
    name: 'Finland',
  },
  {
    id: 'FR',
    name: 'France',
  },
  {
    id: 'GF',
    name: 'French Guiana',
  },
  {
    id: 'PF',
    name: 'French Polynesia',
  },
  {
    id: 'TF',
    name: 'French Southern Territories',
  },
  {
    id: 'GA',
    name: 'Gabon',
  },
  {
    id: 'GM',
    name: 'Gambia',
  },
  {
    id: 'GE',
    name: 'Georgia',
  },
  {
    id: 'DE',
    name: 'Germany',
  },
  {
    id: 'GH',
    name: 'Ghana',
  },
  {
    id: 'GI',
    name: 'Gibraltar',
  },
  {
    id: 'GR',
    name: 'Greece',
  },
  {
    id: 'GL',
    name: 'Greenland',
  },
  {
    id: 'GD',
    name: 'Grenada',
  },
  {
    id: 'GP',
    name: 'Guadeloupe',
  },
  {
    id: 'GU',
    name: 'Guam',
  },
  {
    id: 'GT',
    name: 'Guatemala',
  },
  {
    id: 'GG',
    name: 'Guernsey',
  },
  {
    id: 'GN',
    name: 'Guinea',
  },
  {
    id: 'GW',
    name: 'Guinea-Bissau',
  },
  {
    id: 'GY',
    name: 'Guyana',
  },
  {
    id: 'HT',
    name: 'Haiti',
  },
  {
    id: 'HM',
    name: 'Heard Island and McDonald Islands',
  },
  {
    id: 'VA',
    name: 'Holy See',
  },
  {
    id: 'HN',
    name: 'Honduras',
  },
  {
    id: 'HK',
    name: 'Hong Kong',
  },
  {
    id: 'HU',
    name: 'Hungary',
  },
  {
    id: 'IS',
    name: 'Iceland',
  },
  {
    id: 'IN',
    name: 'India',
  },
  {
    id: 'id',
    name: 'Indonesia',
  },
  {
    id: 'IR',
    name: 'Iran, Islamic Republic of',
  },
  {
    id: 'IQ',
    name: 'Iraq',
  },
  {
    id: 'IE',
    name: 'Ireland',
  },
  {
    id: 'IM',
    name: 'Isle of Man',
  },
  {
    id: 'IL',
    name: 'Israel',
  },
  {
    id: 'IT',
    name: 'Italy',
  },
  {
    id: 'JM',
    name: 'Jamaica',
  },
  {
    id: 'JP',
    name: 'Japan',
  },
  {
    id: 'JE',
    name: 'Jersey',
  },
  {
    id: 'JO',
    name: 'Jordan',
  },
  {
    id: 'KZ',
    name: 'Kazakhstan',
  },
  {
    id: 'KE',
    name: 'Kenya',
  },
  {
    id: 'KI',
    name: 'Kiribati',
  },
  {
    id: 'KP',
    name: "Korea, Democratic People's Republic of",
  },
  {
    id: 'KR',
    name: 'Korea, Republic of',
  },
  {
    id: 'KW',
    name: 'Kuwait',
  },
  {
    id: 'KG',
    name: 'Kyrgyzstan',
  },
  {
    id: 'LA',
    name: "Lao People's Democratic Republic",
  },
  {
    id: 'LV',
    name: 'Latvia',
  },
  {
    id: 'LB',
    name: 'Lebanon',
  },
  {
    id: 'LS',
    name: 'Lesotho',
  },
  {
    id: 'LR',
    name: 'Liberia',
  },
  {
    id: 'LY',
    name: 'Libya',
  },
  {
    id: 'LI',
    name: 'Liechtenstein',
  },
  {
    id: 'LT',
    name: 'Lithuania',
  },
  {
    id: 'LU',
    name: 'Luxembourg',
  },
  {
    id: 'MO',
    name: 'Macao',
  },
  {
    id: 'MG',
    name: 'Madagascar',
  },
  {
    id: 'MW',
    name: 'Malawi',
  },
  {
    id: 'MY',
    name: 'Malaysia',
  },
  {
    id: 'MV',
    name: 'Maldives',
  },
  {
    id: 'ML',
    name: 'Mali',
  },
  {
    id: 'MT',
    name: 'Malta',
  },
  {
    id: 'MH',
    name: 'Marshall Islands',
  },
  {
    id: 'MQ',
    name: 'Martinique',
  },
  {
    id: 'MR',
    name: 'Mauritania',
  },
  {
    id: 'MU',
    name: 'Mauritius',
  },
  {
    id: 'YT',
    name: 'Mayotte',
  },
  {
    id: 'MX',
    name: 'Mexico',
  },
  {
    id: 'FM',
    name: 'Micronesia, Federated States of',
  },
  {
    id: 'MD',
    name: 'Moldova, Republic of',
  },
  {
    id: 'MC',
    name: 'Monaco',
  },
  {
    id: 'MN',
    name: 'Mongolia',
  },
  {
    id: 'ME',
    name: 'Montenegro',
  },
  {
    id: 'MS',
    name: 'Montserrat',
  },
  {
    id: 'MA',
    name: 'Morocco',
  },
  {
    id: 'MZ',
    name: 'Mozambique',
  },
  {
    id: 'MM',
    name: 'Myanmar',
  },
  {
    id: 'NA',
    name: 'Namibia',
  },
  {
    id: 'NR',
    name: 'Nauru',
  },
  {
    id: 'NP',
    name: 'Nepal',
  },
  {
    id: 'NL',
    name: 'Netherlands',
  },
  {
    id: 'NC',
    name: 'New Caledonia',
  },
  {
    id: 'NZ',
    name: 'New Zealand',
  },
  {
    id: 'NI',
    name: 'Nicaragua',
  },
  {
    id: 'NE',
    name: 'Niger',
  },
  {
    id: 'NG',
    name: 'Nigeria',
  },
  {
    id: 'NU',
    name: 'Niue',
  },
  {
    id: 'NF',
    name: 'Norfolk Island',
  },
  {
    id: 'MK',
    name: 'North Macedonia',
  },
  {
    id: 'MP',
    name: 'Northern Mariana Islands',
  },
  {
    id: 'NO',
    name: 'Norway',
  },
  {
    id: 'OM',
    name: 'Oman',
  },
  {
    id: 'PK',
    name: 'Pakistan',
  },
  {
    id: 'PW',
    name: 'Palau',
  },
  {
    id: 'PS',
    name: 'Palestine, State of',
  },
  {
    id: 'PA',
    name: 'Panama',
  },
  {
    id: 'PG',
    name: 'Papua New Guinea',
  },
  {
    id: 'PY',
    name: 'Paraguay',
  },
  {
    id: 'PE',
    name: 'Peru',
  },
  {
    id: 'PH',
    name: 'Philippines',
  },
  {
    id: 'PN',
    name: 'Pitcairn',
  },
  {
    id: 'PL',
    name: 'Poland',
  },
  {
    id: 'PT',
    name: 'Portugal',
  },
  {
    id: 'PR',
    name: 'Puerto Rico',
  },
  {
    id: 'QA',
    name: 'Qatar',
  },
  {
    id: 'RO',
    name: 'Romania',
  },
  {
    id: 'RU',
    name: 'Russian Federation',
  },
  {
    id: 'RW',
    name: 'Rwanda',
  },
  {
    id: 'RE',
    name: 'Réunion',
  },
  {
    id: 'BL',
    name: 'Saint Barthélemy',
  },
  {
    id: 'SH',
    name: 'Saint Helena, Ascension and Tristan da Cunha',
  },
  {
    id: 'KN',
    name: 'Saint Kitts and Nevis',
  },
  {
    id: 'LC',
    name: 'Saint Lucia',
  },
  {
    id: 'MF',
    name: 'Saint Martin (French part)',
  },
  {
    id: 'PM',
    name: 'Saint Pierre and Miquelon',
  },
  {
    id: 'VC',
    name: 'Saint Vincent and the Grenadines',
  },
  {
    id: 'WS',
    name: 'Samoa',
  },
  {
    id: 'SM',
    name: 'San Marino',
  },
  {
    id: 'ST',
    name: 'Sao Tome and Principe',
  },
  {
    id: 'SA',
    name: 'Saudi Arabia',
  },
  {
    id: 'SN',
    name: 'Senegal',
  },
  {
    id: 'RS',
    name: 'Serbia',
  },
  {
    id: 'SC',
    name: 'Seychelles',
  },
  {
    id: 'SL',
    name: 'Sierra Leone',
  },
  {
    id: 'SG',
    name: 'Singapore',
  },
  {
    id: 'SX',
    name: 'Sint Maarten (Dutch part)',
  },
  {
    id: 'SK',
    name: 'Slovakia',
  },
  {
    id: 'SI',
    name: 'Slovenia',
  },
  {
    id: 'SB',
    name: 'Solomon Islands',
  },
  {
    id: 'SO',
    name: 'Somalia',
  },
  {
    id: 'ZA',
    name: 'South Africa',
  },
  {
    id: 'GS',
    name: 'South Georgia and the South Sandwich Islands',
  },
  {
    id: 'SS',
    name: 'South Sudan',
  },
  {
    id: 'ES',
    name: 'Spain',
  },
  {
    id: 'LK',
    name: 'Sri Lanka',
  },
  {
    id: 'SD',
    name: 'Sudan',
  },
  {
    id: 'SR',
    name: 'Suriname',
  },
  {
    id: 'SJ',
    name: 'Svalbard and Jan Mayen',
  },
  {
    id: 'SE',
    name: 'Sweden',
  },
  {
    id: 'CH',
    name: 'Switzerland',
  },
  {
    id: 'SY',
    name: 'Syrian Arab Republic',
  },
  {
    id: 'TW',
    name: 'Taiwan, Province of China',
  },
  {
    id: 'TJ',
    name: 'Tajikistan',
  },
  {
    id: 'TZ',
    name: 'Tanzania, United Republic of',
  },
  {
    id: 'TH',
    name: 'Thailand',
  },
  {
    id: 'TL',
    name: 'Timor-Leste',
  },
  {
    id: 'TG',
    name: 'Togo',
  },
  {
    id: 'TK',
    name: 'Tokelau',
  },
  {
    id: 'TO',
    name: 'Tonga',
  },
  {
    id: 'TT',
    name: 'Trinidad and Tobago',
  },
  {
    id: 'TN',
    name: 'Tunisia',
  },
  {
    id: 'TR',
    name: 'Turkey',
  },
  {
    id: 'TM',
    name: 'Turkmenistan',
  },
  {
    id: 'TC',
    name: 'Turks and Caicos Islands',
  },
  {
    id: 'TV',
    name: 'Tuvalu',
  },
  {
    id: 'UG',
    name: 'Uganda',
  },
  {
    id: 'UA',
    name: 'Ukraine',
  },
  {
    id: 'AE',
    name: 'United Arab Emirates',
  },
  {
    id: 'GB',
    name: 'United Kingdom',
  },
  {
    id: 'UM',
    name: 'United States Minor Outlying Islands',
  },
  {
    id: 'UY',
    name: 'Uruguay',
  },
  {
    id: 'UZ',
    name: 'Uzbekistan',
  },
  {
    id: 'VU',
    name: 'Vanuatu',
  },
  {
    id: 'VE',
    name: 'Venezuela, Bolivarian Republic of',
  },
  {
    id: 'VN',
    name: 'Viet Nam',
  },
  {
    id: 'VG',
    name: 'Virgin Islands, British',
  },
  {
    id: 'VI',
    name: 'Virgin Islands, U.S.',
  },
  {
    id: 'WF',
    name: 'Wallis and Futuna',
  },
  {
    id: 'EH',
    name: 'Western Sahara',
  },
  {
    id: 'YE',
    name: 'Yemen',
  },
  {
    id: 'ZM',
    name: 'Zambia',
  },
  {
    id: 'ZW',
    name: 'Zimbabwe',
  },
];

export default ClientEditBase;

/** Where the marriage was dissolved (Québec DEC), with the old typed place as a hint. */
const DissolutionLocationInput = () => {
  const record = useRecordContext();
  return (
    <SelectInput
      source="marriage_dissolution_location"
      label="Where marriage was dissolved (DEC)"
      choices={DISSOLUTION_LOCATION_CHOICES as unknown as { id: string; name: string }[]}
      emptyText="—"
      parse={(v: string) => v || null}
      fullWidth
      helperText={record?.marriage_dissolution_location ? false : (legacyDissolutionHint(record) ?? false)}
    />
  );
};
